import { storeToRefs } from 'pinia';
import {
    computed,
    getCurrentScope,
    nextTick,
    onBeforeUnmount,
    onScopeDispose,
    onUpdated,
    ref,
    watch,
} from 'vue';
import { ZIndex } from '@/interface/enum';
import { zIndexMapping } from '@/libraries/helpers/zIndexHelper';
import { getScrollTop } from '@/libraries/utils/dom';
import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
import { useHeaderDisplayStore } from '@/stores/headerDisplayStore';

const PROGRAMMATIC_SCROLL_IDLE_MS = 100;

export default function useScroll() {
    const { isShowBetSlip } = storeToRefs(useBetSlipStore());
    const {
        isShowBackToTopScroll,
        sportSwitchTranslateY,
        marketSwitchTranslateY,
        collapseTranslateY,
        genericHeaderTranslateY,
    } = storeToRefs(useHeaderDisplayStore());

    const {
        sportSwitchTop,
        marketSwitchTop,
        collapseTop,
        collapseOpacity,
        genericHeaderTop,
        headerTotalHeight,
        marketTabsHeight,
    } = storeToRefs(useHeaderDisplayStore());

    const { setState, syncScrollY, updateScrollY } = useHeaderDisplayStore();
    let isProgrammaticOddsScroll = false;
    let programmaticScrollEndTimer = 0;

    function stopProgrammaticOddsScroll() {
        isProgrammaticOddsScroll = false;
        window.clearTimeout(programmaticScrollEndTimer);
        window.removeEventListener('scroll', handleProgrammaticOddsScroll);
        window.removeEventListener('wheel', stopProgrammaticOddsScroll);
        window.removeEventListener('touchstart', stopProgrammaticOddsScroll);
    }

    function scheduleProgrammaticOddsScrollEnd() {
        window.clearTimeout(programmaticScrollEndTimer);
        programmaticScrollEndTimer = window.setTimeout(
            stopProgrammaticOddsScroll,
            PROGRAMMATIC_SCROLL_IDLE_MS,
        );
    }

    function handleProgrammaticOddsScroll() {
        const scrollY = getScrollTop();
        if (scrollY >= 0) {
            syncScrollY(scrollY);
        }
        scheduleProgrammaticOddsScrollEnd();
    }

    function startProgrammaticOddsScroll() {
        stopProgrammaticOddsScroll();
        isProgrammaticOddsScroll = true;
        window.addEventListener('scroll', handleProgrammaticOddsScroll, { passive: true });
        window.addEventListener('wheel', stopProgrammaticOddsScroll, { passive: true });
        window.addEventListener('touchstart', stopProgrammaticOddsScroll, { passive: true });
        scheduleProgrammaticOddsScrollEnd();
    }

    if (getCurrentScope() !== undefined) {
        onScopeDispose(stopProgrammaticOddsScroll);
    }

    const isAtTopScreen = ref(true);
    const isShowBackToTop = computed(() => !isAtTopScreen.value && isShowBackToTopScroll.value);
    const sportSwitchStyle = computed(() => ({
        'z-index': zIndexMapping[ZIndex.HeaderSportSwitch],
        'top': `${sportSwitchTop.value}px`,
        'transform': `translateY(${sportSwitchTranslateY.value}px)`,
    }));
    const marketSwitchStyle = computed(() => ({
        'z-index': zIndexMapping[ZIndex.HeaderMarketSwitch],
        'top': `${marketSwitchTop.value}px`,
        'transform': `translateY(${marketSwitchTranslateY.value}px)`,
    }));
    const collapseStyle = computed(() => ({
        'z-index': zIndexMapping[ZIndex.HeaderCollapse],
        'top': `${collapseTop.value}px`,
        'transform': `translateY(${collapseTranslateY.value}px)`,
        'box-shadow': collapseOpacity.value === 0 ? 'none' : '0 0 2rem rgba(0, 0, 0, 0.2)',
        'background-color': collapseOpacity.value === 0 ? 'rgba(246, 246, 246, .6)' : 'rgba(255, 255, 255, .8)',
    }));

    const genericHeaderStyle = computed(() => ({
        'z-index': zIndexMapping[ZIndex.GenericHeader],
        'top': `${genericHeaderTop.value}px`,
        'transform': `translateY(${genericHeaderTranslateY.value}px)`,
    }));

    const marketTabsStyle = computed(() => ({
        'z-index': zIndexMapping[ZIndex.GenericHeader],
        'top': `${marketTabsHeight.value + genericHeaderTranslateY.value - 2}px`,
    }));
    function initHeaderDisplayHeight(
        getElement: () => HTMLElement | null,
        headerDisplayHeightKey: string,
        options: { observeResize?: boolean } = {},
    ) {
        function setHeight(height: number) {
            setState({ [headerDisplayHeightKey]: height });
        }

        function updateHeight() {
            setHeight(getElement()?.clientHeight ?? 0);
        }

        nextTick(() => {
            updateHeight();
        });
        onUpdated(() => nextTick(() => {
            updateHeight();
        }));

        let resizeObserver: ResizeObserver | undefined;
        if (options.observeResize) {
            const element = getElement();
            if (element) {
                resizeObserver = new ResizeObserver(updateHeight);
                resizeObserver.observe(element);
            }
        }

        onBeforeUnmount(() => {
            resizeObserver?.disconnect();
            setHeight(0);
        });
    }

    function handleScrollEvent() {
        const scrollY = getScrollTop();
        if (scrollY < 0) return;
        if (isProgrammaticOddsScroll) {
            syncScrollY(scrollY);
            scheduleProgrammaticOddsScrollEnd();
            return;
        }
        updateScrollY(scrollY);
    }

    function handleScrollBackToTop() {
        isAtTopScreen.value = getScrollTop() < 300;
    }

    async function handleOpenTicketScrollOdds(oddsY: number) {
        if (!oddsY || oddsY <= 0) {
            return;
        }

        await nextTick();

        const betSlip = document.querySelector<HTMLElement>('[data-bet-slip-scroll-boundary]');
        if (!betSlip) return;

        const oddsHeight = 38;
        const margin = 30;
        const lowerBoundary = betSlip.getBoundingClientRect().top - oddsHeight - margin;
        const upperBoundary = headerTotalHeight.value + margin;

        let scroll;
        if (oddsY > lowerBoundary) {
            scroll = oddsY - lowerBoundary;
        } else if (oddsY < upperBoundary) {
            scroll = oddsY - upperBoundary;
        }

        if (scroll) {
            startProgrammaticOddsScroll();
            window?.scrollBy({
                top: scroll,
                left: 0,
                behavior: 'smooth',
            });
        }
    }

    // trigger the recalculation
    watch(isShowBetSlip, () => {
        updateScrollY(window.scrollY ?? 0);
    });

    return {
        isShowBackToTop,
        sportSwitchStyle,
        marketSwitchStyle,
        collapseStyle,
        genericHeaderStyle,
        marketTabsStyle,
        initHeaderDisplayHeight,
        handleScrollEvent,
        handleScrollBackToTop,
        handleOpenTicketScrollOdds,
    };
}

export function handleScrollSportsSwitch(sportsSwitch: HTMLElement, toSport: HTMLElement) {
    const sportsSwitchScrollLeft = sportsSwitch.scrollLeft;
    const scrollLeft = toSport.offsetLeft;

    sportsSwitch?.scrollBy({
        left: scrollLeft - sportsSwitchScrollLeft - 30,
        top: 0,
        behavior: 'smooth',
    });
}
